import { useIdentity } from '@gov-nx/auth/common/hooks/useIdentity';
import { useAutorizaceContext } from '@gov-nx/component/common/Autorizace/Context';
import { AuthorizationType } from '@gov-nx/component/common/Autorizace/types';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import { is } from '@gov-nx/core/types/helpers.types';
import { PageCode } from '@gov-nx/module/page/definitions/codes';
import { ErrorStatusBanner } from '@gov-nx/ui/web/Banner/ErrorStatusBanner';
import { Dialog } from '@gov-nx/ui/web/Dialog';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { AuthorizationMethods } from './AuthorizationMethods';
import { AuthorizationSkeleton } from './Skeleton';

export const AuthorizationDialog = () => {
	const authContext = useAutorizaceContext();
	const { isAuthenticated } = useIdentity();
	const { t } = useLocale(authContext?.code ?? PageCode['autorizace']);

	if (!is(authContext)) {
		return;
	}

	if (!isAuthenticated) {
		return;
	}

	// `isIdle` covers the tick between the dialog opening and the start request
	// entering loading, so the skeleton shows instead of an empty method list.
	const loading =
		authContext.startAuthorization.isLoading ||
		authContext.startAuthorization.isIdle;
	const isError = authContext.startAuthorization.isError;

	const allowedAuthorizationTypes =
		authContext.startAuthorization.data?.data.metodyAutorizace?.map(
			(method) => AuthorizationType[method]
		) ?? [];

	return (
		<Dialog
			isOpen
			blockClose={authContext.confirmAuthCode.isLoading}
			title={<h3>{t('dialog.title')}</h3>}
			onClose={authContext.onDialogCloseClick}>
			<MarkdownRender
				disableWrapper
				applyClasses={{ paragraph: 'mb-(--spacing-l)! text-body-xl!' }}>
				{t('dialog.description')}
			</MarkdownRender>
			{loading && <AuthorizationSkeleton />}
			{!loading && isError && (
				<ErrorStatusBanner code={LocalizeNameSpaceTypes.Global} />
			)}
			{!loading && !isError && (
				<AuthorizationMethods
					code={authContext.code}
					allowedAuthorizationTypes={allowedAuthorizationTypes}
				/>
			)}
		</Dialog>
	);
};
